import { Controller } from "@hotwired/stimulus"

// Click-to-play facade for a YouTube embed. The page ships a poster surface
// (thumbnail + play button) and this controller swaps that element for the
// privacy-enhanced player iframe on activation, in place, so the iframe
// inherits the frame's existing sizing rules and nothing reflows. The swap is
// reversed on teardown and before Turbo caches the page, so a restored page
// never comes back with a player already loaded.
//
// Mounted with no facade target (no button, no action) it does only the
// poster work at the bottom of this file and never creates an iframe — the
// cards that link out rather than play in place (the recent-shorts strips,
// the práctica cards) use it that way.
//
// The facade LOADS the player; it does not start it. YouTube only counts a
// playback toward the video's view count when it is initiated through the
// player's own native play button, and autoplayed embeds never increment
// views — so the default params carry no autoplay, and the tap that counts is
// the viewer's press on YouTube's button, not ours. enablejsapi=1 (with the
// page's origin, appended in #play) lets analytics that listen to the player
// (GA4's enhanced-measurement video events) see that playback. A surface that
// genuinely wants the one-tap start can still ask for it per instance through
// `data-lesson-video-params-value` (e.g. "…&autoplay=1"); the value is used
// verbatim and only the origin is appended.
//
// Expected markup:
//   <div class="watch-card" data-controller="lesson-video"
//        data-lesson-video-id-value="aBc123XyZ90"
//        data-lesson-video-title-value="Direct object pronouns"
//        data-lesson-video-poster-fallback-value="https://img.youtube.com/vi/aBc123XyZ90/hqdefault.jpg">
//     <button type="button" data-lesson-video-target="facade"
//             data-action="lesson-video#play">
//       <img data-lesson-video-target="poster" src="…/maxresdefault.jpg" alt="">
//     </button>
//   </div>
export default class extends Controller {
  static targets = ["facade", "poster"]
  static values = {
    id: String,
    title: String,
    posterFallback: String,
    // The poster came from an editor's upload, not from the thumbnail host —
    // see #checkPoster.
    override: Boolean,
    // No autoplay by default — see the header. Overridden verbatim per
    // instance; #play appends the page's origin either way.
    params: { type: String, default: "rel=0&playsinline=1&enablejsapi=1" }
  }

  connect() {
    this.iframe = null
    this.facade = null
    this.restore = this.restore.bind(this)
    this.degradePoster = this.degradePoster.bind(this)
    this.checkPoster = this.checkPoster.bind(this)

    document.addEventListener("turbo:before-cache", this.restore)

    if (this.hasPosterTarget && this.posterFallbackValue) {
      this.posterTarget.addEventListener("error", this.degradePoster)
      this.posterTarget.addEventListener("load", this.checkPoster)
      if (this.posterTarget.complete) this.checkPoster()
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.restore)
    if (this.hasPosterTarget) {
      this.posterTarget.removeEventListener("error", this.degradePoster)
      this.posterTarget.removeEventListener("load", this.checkPoster)
    }
    this.restore()
  }

  play() {
    if (!this.idValue || !this.hasFacadeTarget) return

    const iframe = document.createElement("iframe")
    // YouTube recommends `origin` whenever enablejsapi=1 is set — it names the
    // page the player may exchange API messages with, and a value that
    // disagrees with the real origin gets those messages dropped. Reading it
    // here, rather than from markup, guarantees it is the origin this document
    // was actually served from (scheme + host + port, no path).
    iframe.src =
      `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.idValue)}` +
      `?${this.paramsValue}&origin=${window.location.origin}`
    iframe.title = this.titleValue
    // `autoplay` stays in the allow list: it is inert without autoplay=1 in the
    // params, and an instance that asks for autoplay needs it.
    iframe.setAttribute(
      "allow",
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    )
    iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin")
    iframe.setAttribute("allowfullscreen", "")

    this.facade = this.facadeTarget
    this.iframe = iframe
    this.facade.replaceWith(iframe)
    // The activated control just left the document, so move focus onto the
    // player rather than letting it fall back to <body>. An iframe is natively
    // focusable and in the tab order, and focusing it focuses the document it
    // contains — so it needs no tabindex of its own. Do NOT add tabindex="-1"
    // here: a negative tabindex on a frame takes the frame's CONTENTS out of
    // sequential navigation, and the player's play/pause, seek, captions and
    // fullscreen controls stop being reachable by keyboard.
    iframe.focus()
  }

  // Puts the poster surface back where the player is, leaving the element in
  // the state it shipped in.
  restore() {
    if (!this.iframe || !this.facade) return
    this.iframe.replaceWith(this.facade)
    this.iframe = null
  }

  // A poster that never loaded, or that loaded as the thumbnail host's tiny
  // "no such image" stand-in (it answers a missing variant with a real 120x90
  // picture, so the load succeeds and no error ever fires), is not a poster.
  static PLACEHOLDER_MAX_WIDTH = 120

  // The size test reads a NARROW image as the thumbnail host's stand-in. That
  // inference only holds for an image served by that host: a poster an editor
  // uploaded is a decision, and a small one is still the picture they chose, so
  // measuring it would silently replace their work with a frame of the video.
  // The `error` listener still covers the case that matters for an uploaded
  // poster — one that genuinely fails to load.
  checkPoster() {
    if (!this.hasPosterTarget || this.overrideValue) return
    const width = this.posterTarget.naturalWidth
    if (width === 0 || width <= this.constructor.PLACEHOLDER_MAX_WIDTH) this.degradePoster()
  }

  // The highest-resolution thumbnail does not exist for every video; swap in
  // the always-present one the first time the poster fails to load.
  degradePoster() {
    if (!this.hasPosterTarget) return
    this.posterTarget.removeEventListener("error", this.degradePoster)
    this.posterTarget.removeEventListener("load", this.checkPoster)
    this.posterTarget.src = this.posterFallbackValue
  }
}
